import useSWRMutation, { type SWRMutationConfiguration } from 'swr/mutation';
import denApi from '@/lib/api/denApi';
import { type ExecuteAction } from '@mls-wallet/shared-types';

/**
 * Response from execute endpoint after the async execution flow change.
 * Contains the guardianTransactionId for polling execution status.
 */
interface ExecuteProposalResponse {
  guardianTransactionId: string;
}

type ExecutePolicyProposalArg = {
  proposalId: string;
  action: ExecuteAction;
};

const fetcher = async (
  _key: string,
  { arg }: { arg: ExecutePolicyProposalArg }
): Promise<ExecuteProposalResponse> => {
  const { proposalId, action } = arg;
  return await denApi.post<ExecuteProposalResponse, { action: ExecuteAction }>(
    `admin/policy/proposals/${encodeURIComponent(proposalId)}/execute`,
    {
      action,
    }
  );
};

/**
 * Hook to execute a policy proposal (approve or reject).
 *
 * For APPROVE action:
 * - After calling executeProposal, the response contains a guardianTransactionId
 *   that should be used with useGuardianTransactionStatus to poll execution status.
 *
 * For REJECT action:
 * - Can only be called once the proposal has received enough rejection signatures.
 * - Returns a guardianTransactionId for async execution tracking via polling.
 *
 * @example
 * ```tsx
 * const { executeProposal } = useExecutePolicyProposal();
 * const { startExecution } = useExecution();
 *
 * const handleExecute = (action: ExecuteAction) => {
 *   startExecution({
 *     executionTarget: 'proposal',
 *     executionTargetId: proposalId,
 *     proposalType: 'policy',
 *     request: () => executeProposal({ proposalId, action }),
 *   });
 * };
 * ```
 */
export function useExecutePolicyProposal(
  swrOptions?: SWRMutationConfiguration<
    ExecuteProposalResponse,
    Error,
    string,
    ExecutePolicyProposalArg
  >
) {
  const { trigger, isMutating, error } = useSWRMutation<
    ExecuteProposalResponse,
    Error,
    string,
    ExecutePolicyProposalArg
  >('admin/policy/proposals/execute', fetcher, swrOptions);

  return { executeProposal: trigger, isLoading: isMutating, error };
}
